import { z } from 'zod';

/**
 * Phase 25 — EventoTimelineService cross-boundary type contract.
 *
 * Single source of truth for the Tab Atividades timeline rows. Backend
 * `EventoTimelineService` ($queryRaw UNION ALL across EventoAudit /
 * CandidaturaAudit / Movimentacao / PagamentoEvento) and frontend
 * `EventoAtividadesTabComponent` both consume these types verbatim.
 *
 * Discriminator: `kind` (lowercase snake_case per D-05).
 *
 * Pitfall 4 reminder: BIGINT columns (`valorCents` on Movimentacao + Pagamento)
 * round-trip through JSON as STRING — Postgres `::text` cast inside
 * `jsonb_build_object` → JSON.stringify preserves precision → frontend parses
 * with `parseInt`/`BigInt` as needed.
 *
 * D-11 reminder: `actorName` is ALWAYS present on TimelineRowBase. The backend
 * resolves it via `renderActorName(actorId, actors.get(actorId)?.email…)` and
 * sends the resolved string — UI never re-resolves.
 */

export const TimelineKindEnum = z.enum([
  'evento_audit',
  'candidatura_audit',
  'movimentacao',
  'pagamento',
]);
export type TimelineKind = z.infer<typeof TimelineKindEnum>;

// ---------- Per-kind payload schemas (D-05) ----------

export const EventoAuditPayloadSchema = z.object({
  field: z.string(),
  oldValue: z.unknown().nullable(),
  newValue: z.unknown().nullable(),
});
export type EventoAuditPayload = z.infer<typeof EventoAuditPayloadSchema>;

export const CandidaturaAuditPayloadSchema = z.object({
  candidaturaId: z.string(),
  field: z.string(),
  oldValue: z.unknown().nullable(),
  newValue: z.unknown().nullable(),
});
export type CandidaturaAuditPayload = z.infer<typeof CandidaturaAuditPayloadSchema>;

// Pitfall 4: `valorCents` is STRING to preserve BIGINT precision through JSON.
export const MovimentacaoPayloadSchema = z.object({
  tipo: z.string(),
  status: z.string(),
  valorCents: z.string(),
  descricao: z.string().nullable(),
  referencia: z.record(z.string(), z.unknown()).nullable(),
});
export type MovimentacaoPayload = z.infer<typeof MovimentacaoPayloadSchema>;

// Pitfall 4: `valorCents` is STRING for the same reason.
export const PagamentoPayloadSchema = z.object({
  status: z.string(),
  valorCents: z.string(),
  chargeId: z.string().nullable(),
  pagoEm: z.string().nullable(), // ISO 8601
  expiraEm: z.string().nullable(), // ISO 8601
});
export type PagamentoPayload = z.infer<typeof PagamentoPayloadSchema>;

// ---------- Discriminated union base + assembly ----------

const TimelineRowBase = z.object({
  id: z.string(),
  createdAt: z.string(), // ISO 8601
  actorId: z.string().nullable(),
  actorName: z.string(), // post renderActorName fallback (D-11)
  eventoId: z.string(),
  profissionalId: z.string().nullable(),
});

export const TimelineRowSchema = z.discriminatedUnion('kind', [
  TimelineRowBase.extend({
    kind: z.literal('evento_audit'),
    payload: EventoAuditPayloadSchema,
  }),
  TimelineRowBase.extend({
    kind: z.literal('candidatura_audit'),
    payload: CandidaturaAuditPayloadSchema,
  }),
  TimelineRowBase.extend({
    kind: z.literal('movimentacao'),
    payload: MovimentacaoPayloadSchema,
  }),
  TimelineRowBase.extend({
    kind: z.literal('pagamento'),
    payload: PagamentoPayloadSchema,
  }),
]);
export type TimelineRow = z.infer<typeof TimelineRowSchema>;

export const TimelineResponseSchema = z.object({
  rows: z.array(TimelineRowSchema),
});
export type TimelineResponse = z.infer<typeof TimelineResponseSchema>;
